import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Share
} from "lucide-react";

export default function InstallPrompt() {
  const location = useLocation();
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const promptRef = useRef(null);

  useEffect(() => {
    // 1. Check if the app is already installed (Standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    
    // 2. Check if screen width is "Mobile" (less than 768px)
    const isMobileWidth = window.innerWidth < 768;

    // 3. Check User Agent (Is it actually a mobile device?)
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isMobileDevice = /iphone|ipad|ipod|android/.test(userAgent);

    // ONLY proceed if it is NOT installed, IS a mobile device, and IS a small screen
    if (!isStandalone && isMobileDevice && isMobileWidth) {
      
      // Check if iOS
      if (/iphone|ipad|ipod/.test(userAgent)) {
        setIsIOS(true);
        setShowPrompt(true);
      }
    }

    // 4. Listen for Android "Add to Home Screen" event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      
      // Only stash the event if we are on mobile width
      if (window.innerWidth < 768) {
        setDeferredPrompt(e);
        setShowPrompt(true);
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const updatePromptOffset = () => {
      const height = promptRef.current?.offsetHeight || 0;
      root.style.setProperty("--install-prompt-height", `${height + 24}px`);
    };

    if (!showPrompt) {
      root.style.setProperty("--install-prompt-height", "0px");
      root.style.setProperty("--login-center-offset", "-2vh");
      return;
    }

    updatePromptOffset();
    root.style.setProperty("--login-center-offset", "0px");
    window.addEventListener("resize", updatePromptOffset);
    return () => {
      window.removeEventListener("resize", updatePromptOffset);
      root.style.setProperty("--install-prompt-height", "0px");
      root.style.setProperty("--login-center-offset", "-2vh");
    };
  }, [showPrompt]);

  const handleAndroidInstall = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the A2HS prompt');
        }
        setDeferredPrompt(null);
        setShowPrompt(false);
      });
    }
  };
  
  // Only allow install prompt on login route.
  if (location.pathname !== "/login") return null;

  // If we shouldn't show it, return null immediately
  if (!showPrompt) return null;

  return (
    <div ref={promptRef} className="fixed bottom-4 left-4 right-4 z-50 animate-bounce-in">
      <div className="bg-gray-900 text-white p-4 rounded-xl shadow-2xl flex flex-col items-start relative border border-gray-700">
        
        {/* Close Button */}
        <button 
          onClick={() => setShowPrompt(false)} 
          className="absolute top-2 right-2 text-gray-400 hover:text-white p-2"
        >
          ✕
        </button>

        <p className="font-bold text-lg mb-1">Install App</p>
        <p className="text-sm text-gray-300 mb-3">
          For the best experience, add this app to your home screen.
        </p>

        {/* ANDROID BUTTON (Only shows if browser supports automatic install) */}
        {deferredPrompt && (
          <button 
            onClick={handleAndroidInstall}
            className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-3 rounded-lg text-sm font-bold w-full shadow-lg"
          >
            Add to Home Screen
          </button>
        )}

        {/* IOS INSTRUCTIONS (Only shows on iPhones) */}
        {isIOS && (
          <div className="text-sm text-gray-300 bg-gray-800 p-3 rounded-lg w-full">
            <div className="flex items-center gap-3 mb-2">
              <span className="font-bold text-gray-400">1</span>
              <span>Tap the</span>
              <Share size={18} className="text-blue-400 mx-1 inline-block" />
              <span>Share button</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-bold text-gray-400">2</span>
              <span>Select</span>
              <span className="font-bold text-white border border-gray-600 px-2 py-0.5 rounded bg-gray-700 text-xs">Add to Home Screen</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
